import { useEffect, useMemo, useRef, useState } from "react";

import { compact } from "lodash";
import { useNavigate } from "react-router-dom";

import { usePageTitle } from "@/atoms/page-title-atom";
import { useUser } from "@components/user-context";
import { CompareDatePicker, DatePicker, MultiSelect } from "@components/v2";
import { TimeGranularityV2Select } from "@components/v2/time-granularity-select/time-granularity-v2-select";
import { useDateRange } from "@features/orders-v2/hooks/use-date-range";
import { OrdersV2SummaryGraph } from "@features/orders-v2/orders-chart";
import { OrdersTable } from "@features/orders-v2/orders-table";
import { OrdersTableSettings } from "@features/orders-v2/orders-table-settings";
import { QUICK_FILTERS } from "@features/orders-v2/quick-filters";
import { QuickFilterSelector } from "@features/sales-v4";
import { useNorthbeamQuery } from "@hooks/http";
import { prettyLogEvent } from "@internal/analytics";
import { type FetchOrderPlatformAndEcommerceOptions } from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import {
  dateRangeToInterval,
  EcomPlatformFriendlyNames,
  parseDateInterval,
  PlatformFriendlyNames,
  todayLocalAsISODate,
} from "@north-beam/nb-common";
import { useReportBodyState } from "@pages/orders/orders-control-state";
import {
  useOrderPageAdPlatforms,
  useOrderPageEcommercePlatforms,
  useOrdersQueryParams,
} from "@pages/orders/use-orders-filters";
import { FETCH_ORDER_PLATFORMS_AND_ECOMMERCE_OPTIONS } from "@pages/settings/queries";
import { reasonableGuessAtNaming } from "@utils/pretty-names";

import { QuickFilterTooltip } from "./quick-filter-tooltip";

export const OrdersV2 = () => {
  const [, setPageTitle] = usePageTitle();

  const { user } = useUser();
  const [selectedQuickFilter, setSelectedQuickFilter] = useState(
    QUICK_FILTERS[0],
  );

  const [query, setQuery] = useOrdersQueryParams();

  const { data: optionsData, loading } = useNorthbeamQuery<
    FetchOrderPlatformAndEcommerceOptions,
    unknown
  >(FETCH_ORDER_PLATFORMS_AND_ECOMMERCE_OPTIONS);

  useEffect(() => {
    setPageTitle("Orders Attribution");
  }, [setPageTitle]);

  const navigate = useNavigate();

  useEffect(() => {
    prettyLogEvent.visited({
      page: Pages.DASHBOARD_V2_ORDERS,
      user,
    });
  }, [user]);

  const [ecommercePlatforms, setEcommercePlatforms] =
    useOrderPageEcommercePlatforms();

  const [adPlatforms, setAdPlatforms] = useOrderPageAdPlatforms();
  const { dateRange, setDateRange, compareDateRange, setCompareDateRange } =
    useDateRange();

  const { timeGranularity, setTimeGranularity } = useReportBodyState();

  const applyQuickFilter = (filter: (typeof QUICK_FILTERS)[0]) => {
    setSelectedQuickFilter(filter);
    if (filter.filters.length > 0) {
      const resetParams = Object.keys(query).reduce((acc, key) => {
        acc[key] = undefined;
        return acc;
      }, {} as Record<string, unknown>);

      const queryParams = filter.filters.reduce((acc, individualFilter) => {
        acc[individualFilter.field] = individualFilter.value;
        return acc;
      }, resetParams);

      setQuery(queryParams);
    } else {
      navigate(window.location.pathname, { replace: true });
    }
  };

  const [isSticky, setIsSticky] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { root: null, threshold: 0, rootMargin: "-150px 0px 0px 0px" },
    );
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const referenceDateRange = useMemo(() => {
    if (typeof dateRange === "string") {
      return parseDateInterval(
        dateRangeToInterval(dateRange),
        todayLocalAsISODate(),
      );
    }

    return { startDate: dateRange.startDate, endDate: dateRange.endDate };
  }, [dateRange]);

  return (
    <div className="flex flex-col h-[calc(100%-84px)] bg-v2-glass-inverse z-1 relative">
      <div
        ref={headerRef}
        className={`flex px-6 items-center py-4.5 gap-2 sticky top-0 z-50 bg-v2-glass-inverse backdrop-blur-sm transition delay-150 duration-300 ${
          isSticky ? "drop-shadow-[0_0px_10px_rgba(0,0,0,0.1)]" : ""
        }`}
      >
        <DatePicker
          className="w-32"
          value={dateRange}
          onChange={(val) => val && setDateRange(val)}
        />
        <CompareDatePicker
          className="w-48"
          value={compareDateRange}
          onChange={(val) => val && setCompareDateRange(val)}
          referenceDateRange={referenceDateRange}
          timezone={user.timezone}
          timeGranularity={timeGranularity}
        />
        <TimeGranularityV2Select
          className="w-32 h-[36px]"
          timeGranularity={timeGranularity}
          onChange={setTimeGranularity}
        />
        <MultiSelect
          values={compact(ecommercePlatforms)}
          placeholder="E-Com Platforms"
          buttonClassName="w-32 h-[36px]"
          disabled={loading}
          options={(optionsData?.me.ecommercePlatforms || []).map((o) => ({
            value: o,
            label: EcomPlatformFriendlyNames[o] || reasonableGuessAtNaming(o),
          }))}
          onChange={setEcommercePlatforms}
        />
        <MultiSelect
          buttonClassName="w-32 h-[36px]"
          placeholder="Ad Platforms"
          disabled={loading}
          values={compact(adPlatforms)}
          options={(optionsData?.me.adPlatforms || []).map((o) => ({
            value: o,
            label: PlatformFriendlyNames[o] || reasonableGuessAtNaming(o),
          }))}
          onChange={setAdPlatforms}
        />
        <QuickFilterSelector
          options={QUICK_FILTERS.map((e) => ({ ...e, isDefault: true }))}
          value={selectedQuickFilter}
          onChange={applyQuickFilter}
          tooltipComponent={(filter) => <QuickFilterTooltip filter={filter} />}
        />
      </div>
      <div className="flex flex-col gap-6">
        <div className="flex gap-4 px-6">
          <div className="flex-1 overflow-hidden">
            <OrdersV2SummaryGraph />
          </div>
        </div>
        <div className="flex flex-col gap-4 flex-grow relative px-6">
          <OrdersTableSettings
            selectedQuickFilter={selectedQuickFilter}
            showMaximize
          />
          <OrdersTable
            className="mb-4 min-h-[500px]"
            tableClassName="flex-grow"
            selectedQuickFilter={selectedQuickFilter}
          />
        </div>
      </div>
    </div>
  );
};
